/**
 * Computes a nice Y-axis scale from series data so the chart fits the data
 * and low values remain visible (smaller tick steps).
 *
 * @param {Array<{ data: number[] }>} series - Chart series (e.g. [{ name: 'A', data: [10, 20] }])
 * @param {{ isPercentage?: boolean }} [opts] - If true, cap max at 100 and use %-friendly ticks
 * @returns {{ min: number, max: number, tickAmount: number, forceNiceScale?: boolean }}
 */
export function computeYAxisScaleFromSeries(series, opts = {}) {
    const isPercentage = opts.isPercentage === true
    const values = (Array.isArray(series) ? series : [])
        .flatMap((s) => (s && Array.isArray(s.data) ? s.data : []))
        .filter((n) => typeof n === 'number' && !Number.isNaN(n))

    const dataMax = values.length ? Math.max(...values) : 0

    if (dataMax <= 0) {
        return isPercentage
            ? { min: 0, max: 100, tickAmount: 10, forceNiceScale: true }
            : { min: 0, max: 1, tickAmount: 5, forceNiceScale: true }
    }

    const padded = dataMax * 1.1
    let max = niceCeiling(padded)
    if (!isPercentage && max > dataMax * 1.25) {
        max = niceCeiling(dataMax * 1.2)
    }
    if (isPercentage) {
        max = Math.min(100, Math.max(max, 10))
    }

    const tickAmount = computeTickAmount(max, dataMax, isPercentage)

    return {
        min: 0,
        max,
        tickAmount,
        forceNiceScale: true,
    }
}

/**
 * Round up to a "nice" axis max (e.g. 437 -> 500, 505 -> 600, 23 -> 25, 0.8 -> 1).
 * Uses steps 1, 2, 5, 6, 8, 10 to avoid excessive white space (e.g. 555 no longer becomes 1000).
 */
function niceCeiling(x) {
    if (x <= 0) return 1
    const exp = Math.floor(Math.log10(x))
    const magnitude = exp >= 0 ? Math.pow(10, exp) : 1 / Math.pow(10, -exp)
    const normalized = x / magnitude
    if (normalized <= 1) return magnitude
    if (normalized <= 2) return 2 * magnitude
    if (normalized <= 5) return 5 * magnitude
    if (normalized <= 6) return 6 * magnitude
    if (normalized <= 8) return 8 * magnitude
    return 10 * magnitude
}

/**
 * More ticks when max is small so low values and labels stay visible.
 */
function computeTickAmount(max, dataMax, isPercentage) {
    if (isPercentage) {
        if (max <= 10) return 5
        if (max <= 25) return 5
        if (max <= 50) return 5
        return 10
    }
    if (max <= 1) return 5
    if (max <= 5) return 5
    if (max <= 20) return 10
    return 10
}
